import { InputModal } from "@/components/InputModal";
import { AppointmentModal } from "@/features/appointments/modals/AppointmentModal";
import { CalendarAppointmentModal } from "@/features/appointments/modals/CalendarAppointmentModal";
import { SearchAppointmentModal } from "@/features/appointments/modals/searchAppointment/SearchAppointmentModal";
import { ConsentSettingModal } from "@/features/consent/ConsentSettingsModal";
import { AddDrawingModal } from "@/features/drawings/modals/addDrawings/AddDrawingModal";
import { ManageDrawingModal } from "@/features/drawings/modals/manageDrawings/ManageDrawingModal";
import { SpokeWithCustomerModal } from "@/features/modals/SpokeWithCustomerModal";
import { SelectionModal } from "@/features/modals/selectionModal/SelectionModal";
import { ViewDraftOrderModal } from "@/features/orders/draft/ViewDraftOrderModal";
import { ViewOrderModal } from "@/features/orders/order/ViewOrderModal";
import { ViewQuotationModal } from "@/features/orders/quotation/ViewQuotationModal";
import { ActiveProjectProvider } from "@/features/project/ActiveProject";
import { ProjectProgressModal } from "@/features/projects/cards/ProjectProgressModal";
import { Quickview } from "@/features/projects/modals/Quickview";
import { AnswersProvider } from "@/features/questionnaire/AnswersProvider";
import { TaskModal } from "@/features/tasks/taskModal/TaskModal";
import {
  ConfirmModal,
  ConsentModal,
  CustomerProfile,
  NewProjectModal,
  SelectCoworkerAndTeamModal,
  SelectDepartmentModal,
} from "@repo/ui";
import { assertUnreachable } from "@repo/utils";

import { OutlookEventModal } from "./OutlookEventModal";
import { QuotationsModal } from "./QuotationsModal";
import { SelectTagsModal } from "./TagsModal/SelectTagsModal";
import { BookAppointmentModal } from "./bookAppointmentModal/BookAppointmentModal";
import { CmpSelectionModal } from "./cmpSelectionModal/CmpSelectionModal";
import { CustomerModal } from "./customerModal/CustomerModal";
import { ClassificationModal } from "./needAnalysisModal/ClassificationModal";
import { NeedAnalysisModal } from "./needAnalysisModal/NeedAnalysisModal";
import { QuestionnaireModal } from "./needAnalysisModal/QuestionnaireModal";
import { ServiceInterestedInModal } from "./needAnalysisModal/ServiceInterestedInModal";
import { ProjectDetailsModal } from "./projectDetailsModal/ProjectDetailsModal";
import { type ModalDefinition } from "./types";

export const renderModalDetails = (
  modal: ModalDefinition | undefined,
  hideModal: (modalType: ModalDefinition["type"]) => void
) => {
  if (!modal) {
    return null;
  }

  if (modal.type === "BOOK_APPOINTMENT") {
    return (
      <BookAppointmentModal
        isOpen={true}
        closeModal={() => hideModal("BOOK_APPOINTMENT")}
      />
    );
  }

  if (modal.type === "NEED_ANALYSIS") {
    return (
      <NeedAnalysisModal
        isOpen={true}
        closeModal={() => hideModal("NEED_ANALYSIS")}
        focusField={modal.focusField}
      />
    );
  }
  if (modal.type === "PROJECT_PROGRESS") {
    return (
      <ProjectProgressModal
        isOpen={true}
        closeModal={() => hideModal("PROJECT_PROGRESS")}
        updateStatus={modal.updateStatus}
        currentStatus={modal.currentStatus}
      />
    );
  }

  if (modal.type === "PROJECT_DETAILS") {
    return (
      <ProjectDetailsModal
        isOpen={true}
        closeModal={() => hideModal("PROJECT_DETAILS")}
      />
    );
  }

  if (modal.type === "PROJECT_CUSTOMER") {
    return (
      <CustomerModal
        isOpen={true}
        closeModal={() => hideModal("PROJECT_CUSTOMER")}
      />
    );
  }

  if (modal.type === "CLASSIFICATION") {
    return (
      <ClassificationModal
        isOpen={true}
        closeModal={() => hideModal("CLASSIFICATION")}
        classification={modal.classification}
      />
    );
  }

  if (modal.type === "APPOINTMENT") {
    return (
      <AppointmentModal
        isOpen={true}
        closeModal={() => hideModal("APPOINTMENT")}
        appointment={modal.appointment}
        historyEntries={modal.historyEntries}
        showActions={modal.showActions}
        projectId={modal.projectId}
      />
    );
  }

  if (modal.type === "ORDER") {
    return (
      <ViewOrderModal
        isOpen={true}
        closeModal={() => hideModal("ORDER")}
        orderId={modal.orderId}
        historyEntries={modal.historyEntries}
      />
    );
  }

  if (modal.type === "SEARCH_APPOINTMENT") {
    return (
      <SearchAppointmentModal
        isOpen={true}
        closeModal={() => hideModal("SEARCH_APPOINTMENT")}
        projectId={modal.projectId}
        initialSearchTerm={modal.initialSearchTerm}
        initialAppointments={modal.initialAppointments}
        onBack={modal.onBack}
      />
    );
  }

  if (modal.type === "PLANNER") {
    return (
      <ManageDrawingModal
        isOpen={true}
        closeModal={() => hideModal("PLANNER")}
        drawing={modal.drawing}
        historyEntries={modal.historyEntries}
        onDisassociate={modal.onDisassociate}
        disableAction={modal.disableAction}
      />
    );
  }

  if (modal.type === "TASK") {
    return (
      <TaskModal
        isOpen={true}
        closeModal={() => hideModal("TASK")}
        task={modal.task}
        initialData={modal.initialData}
        opportunityId={modal.opportunityId}
        mode={modal.mode}
        onBack={modal.onBack}
        historyEntries={modal.historyEntries}
        mutateSearchAppointments={modal.mutateSearchAppointments}
        context={modal.context}
        taskDetails={modal.taskDetails}
      />
    );
  }

  if (modal.type === "SPOKE_WITH_CUSTOMER") {
    return (
      <SpokeWithCustomerModal
        isOpen={true}
        closeModal={() => hideModal("SPOKE_WITH_CUSTOMER")}
        onBack={modal.onBack}
      />
    );
  }

  if (modal.type === "QUICKVIEW") {
    return (
      <ActiveProjectProvider opportunityId={modal.opportunityId}>
        <Quickview
          opportunityId={modal.opportunityId}
          closeQuickView={() => hideModal("QUICKVIEW")}
        />
      </ActiveProjectProvider>
    );
  }
  if (modal.type === "SERVICES_INTERESTED") {
    return (
      <ServiceInterestedInModal
        isOpen={true}
        closeModal={() => hideModal("SERVICES_INTERESTED")}
        services={modal.servicesInterested}
        onSave={modal.onSave}
      />
    );
  }

  if (modal.type === "CONFIRM") {
    return (
      <ConfirmModal
        visible={true}
        onClose={() => hideModal("CONFIRM")}
        onConfirm={modal.onConfirm}
        onCancel={() => hideModal("CONFIRM")}
        title={modal.title}
        message={modal.message}
      />
    );
  }

  if (modal.type === "INPUT_VALUE") {
    return (
      <InputModal
        visible={true}
        onClose={() => hideModal("INPUT_VALUE")}
        onSave={modal.onSave}
        onCancel={() => hideModal("INPUT_VALUE")}
        title={modal.title}
        message={modal.message}
        initialValue={modal.initialValue}
      />
    );
  }
  if (modal.type === "OUTLOOK_EVENT") {
    return (
      <OutlookEventModal
        isOpen={true}
        closeModal={() => hideModal("OUTLOOK_EVENT")}
        item={modal.item}
      />
    );
  }

  if (modal.type === "CALENDAR_APPOINTMENT") {
    return (
      <ActiveProjectProvider opportunityId={modal.opportunityId}>
        <CalendarAppointmentModal
          isOpen={true}
          closeModal={() => hideModal("CALENDAR_APPOINTMENT")}
          appointment={modal.appointment}
          mutateSearchAppointments={modal.mutateSearchAppointments}
          onBookFollowUp={modal.onBookFollowUp}
        />
      </ActiveProjectProvider>
    );
  }

  if (modal.type === "SELECT_DEPARTMENT") {
    return (
      <SelectDepartmentModal
        isOpen={true}
        closeModal={() => hideModal("SELECT_DEPARTMENT")}
        selected={modal.selected}
        onUpdate={modal.onUpdate}
        onAsyncUpdate={modal.onAsyncUpdate}
        allowEmpty={modal.allowEmpty}
        useBokaConfig={modal.useBokaConfig}
      />
    );
  }

  if (modal.type === "SELECT_TAG") {
    return (
      <SelectTagsModal
        isOpen={true}
        closeModal={() => hideModal("SELECT_TAG")}
        selected={modal.selected}
        opportunityId={modal.opportunityId}
        onAsyncUpdate={modal.onAsyncUpdate}
      />
    );
  }

  if (modal.type === "NEW_PROJECT") {
    return (
      <NewProjectModal
        isOpen={true}
        associatedTask={modal.associatedTask}
        associatedDropInAppointment={modal.associatedDropInAppointment}
        closeModal={() => hideModal("NEW_PROJECT")}
        onProjectCreated={modal.onProjectCreated}
        onSelectProject={modal.onSelectProject}
        customerInfo={modal.customerInfo}
        departments={modal.departments}
        initialCustomerSearchValue={modal.initialCustomerSearchValue}
        pushModal={modal.pushModal}
        filterCurrentProject={modal.filterCurrentProject}
      />
    );
  }

  if (modal.type === "ADD_DRAWING") {
    return (
      <AddDrawingModal
        isOpen={true}
        closeModal={() => hideModal("ADD_DRAWING")}
        opportunityId={modal.opportunityId}
        existingDrawingIds={modal.existingDrawingIds}
        filterAssociated={modal.filterAssociated}
        customer={modal.customer}
      />
    );
  }

  if (modal.type === "QUESTIONNAIRE") {
    return (
      <AnswersProvider>
        <QuestionnaireModal
          isOpen={true}
          closeModal={() => hideModal("QUESTIONNAIRE")}
          opportunityId={modal.opportunityId}
          selectedHfbs={modal.selectedHfbs}
          service={modal.service}
        />
      </AnswersProvider>
    );
  }

  if (modal.type === "SELECT_TEAM_OR_COWORKER") {
    return (
      <SelectCoworkerAndTeamModal
        isOpen={true}
        closeModal={() => hideModal("SELECT_TEAM_OR_COWORKER")}
        selectionMode={modal.selectionMode}
        selected={modal.selected}
        setSelected={modal.setSelected}
        searchContext={modal.searchContext}
      />
    );
  }

  if (modal.type === "QUOTATIONS") {
    return (
      <QuotationsModal
        isOpen={true}
        closeModal={() => hideModal("QUOTATIONS")}
      />
    );
  }

  if (modal.type === "CUSTOMER_PROFILE") {
    return (
      <CustomerProfile
        pushModal={modal.pushModal}
        onSelectProject={modal.onSelectProject}
        onProjectCreated={modal.onProjectCreated}
        associatedDropInAppointment={modal.associatedDropInAppointment}
        associatedTask={modal.associatedTask}
        initialCustomerSearchValue={modal.initialCustomerSearchValue}
        departments={modal.departments}
      />
    );
  }

  if (modal.type === "CONSENT") {
    return (
      <ConsentModal
        opportunityId={modal.opportunityId}
        isOpen={true}
        closeModal={() => hideModal("CONSENT")}
        customer={modal.customer}
        departments={modal.departments}
        manualConsents={modal.manualConsents}
        onSelectConsents={modal.onSelectConsents}
      />
    );
  }

  if (modal.type === "CONSENT_SETTINGS") {
    return (
      <ConsentSettingModal
        isOpen={true}
        closeModal={() => hideModal("CONSENT_SETTINGS")}
      />
    );
  }

  if (modal.type === "SELECT_CMP") {
    return (
      <CmpSelectionModal
        isOpen={true}
        closeModal={() => hideModal("SELECT_CMP")}
        selectedCmp={modal.selectedCmp}
        onSelect={modal.onSelect}
      />
    );
  }

  if (modal.type === "SELECTION") {
    return (
      <SelectionModal
        isOpen={true}
        closeModal={() => hideModal("SELECTION")}
        title={modal.title}
        allValues={modal.allValues}
        selectedValues={modal.selectedValues}
        onSelect={modal.onSelect}
        showSearch={modal.showSearch}
        selectionMode={modal.selectionMode}
      />
    );
  }

  if (modal.type === "QUOTATION") {
    return (
      <ViewQuotationModal
        isOpen={true}
        closeModal={() => hideModal("QUOTATION")}
        quotationId={modal.quotationId}
        historyEntries={modal.historyEntries}
        activeTab={modal.activeTab}
        projectId={modal.projectId}
      />
    );
  }

  if (modal.type === "DRAFT_ORDER") {
    return (
      <ViewDraftOrderModal
        isOpen={true}
        closeModal={() => hideModal("DRAFT_ORDER")}
        draftOrderId={modal.draftOrderId}
        historyEntries={modal.historyEntries}
        activeTab={modal.activeTab}
      />
    );
  }

  assertUnreachable(modal);
};
